import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { colors } from '../styles/colors';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const isStaging = window.location.hostname.includes('staging');

  const isPathActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const sectionTabs = (() => {
    if (isPathActive('/candidates') || isPathActive('/applies')) {
      return [
        { label: 'Candidatos', path: '/candidates' },
        { label: 'Applies', path: '/applies' },
      ];
    }

    if (isPathActive('/opportunities') || isPathActive('/integration-opportunities')) {
      return [
        { label: 'Oportunidades', path: '/opportunities' },
        { label: 'Integração', path: '/integration-opportunities' },
      ];
    }

    if (
      isPathActive('/companies') ||
      isPathActive('/recruiters') ||
      isPathActive('/company-subscriptions') ||
      isPathActive('/company-credits') ||
      isPathActive('/onboarding-ab')
    ) {
      return [
        { label: 'Empresas', path: '/companies' },
        { label: 'Recrutadores', path: '/recruiters' },
        { label: 'Assinaturas', path: '/company-subscriptions' },
        { label: 'Créditos', path: '/company-credits' },
        { label: 'Onboarding AB', path: '/onboarding-ab' },
      ];
    }

    return [];
  })();

  const isActive = (path: string) => {
    if (path === '/candidates') {
      return isPathActive('/candidates') || isPathActive('/applies');
    }

    if (path === '/opportunities') {
      return isPathActive('/opportunities') || isPathActive('/integration-opportunities');
    }

    if (path === '/companies') {
      return isPathActive('/companies') ||
        isPathActive('/recruiters') ||
        isPathActive('/company-subscriptions') ||
        isPathActive('/company-credits') ||
        isPathActive('/onboarding-ab');
    }

    if (path === '/nps') {
      return isPathActive('/nps');
    }

    return location.pathname === path;
  };

  return (
    <div style={styles.container}>
      {isStaging && (
        <div style={styles.stagingBanner}>
          ⚠️ AMBIENTE DE STAGING - NÃO É PRODUÇÃO
        </div>
      )}
      <div className="colorline"></div>
      <nav style={styles.sidebar}>
        <div style={styles.logo}>
          <h2 style={styles.logoText}>Trampos Admin</h2>
        </div>
        <ul style={styles.nav}>
          <li>
            <Link
              to="/dashboard"
              style={{...styles.navLink, ...(isActive('/dashboard') ? styles.activeLink : {})}}
            >
              Dashboard
            </Link>
          </li>
          <li>
            <Link
              to="/candidates"
              style={{...styles.navLink, ...(isActive('/candidates') ? styles.activeLink : {})}}
            >
              Candidatos
            </Link>
          </li>
          <li>
            <Link
              to="/opportunities"
              style={{...styles.navLink, ...(isActive('/opportunities') ? styles.activeLink : {})}}
            >
              Oportunidades
            </Link>
          </li>
          <li>
            <Link
              to="/companies"
              style={{...styles.navLink, ...(isActive('/companies') ? styles.activeLink : {})}}
            >
              Empresas
            </Link>
          </li>
          <li>
            <Link
              to="/nps"
              style={{...styles.navLink, ...(isActive('/nps') ? styles.activeLink : {})}}
            >
              NPS
            </Link>
          </li>
        </ul>
        <div style={styles.userInfo}>
          <p style={styles.userName}>{user?.name || user?.email}</p>
          {user?.position && (
            <p style={styles.userPosition}>{user.position}</p>
          )}
          {user?.superuser && (
            <span style={styles.superuserBadge}>SUPER</span>
          )}
          <button onClick={logout} style={styles.logoutBtn}>
            Sair
          </button>
        </div>
      </nav>
      <main style={styles.main}>
        {sectionTabs.length > 0 && (
          <div style={styles.sectionTabs}>
            {sectionTabs.map(tab => {
              const selected = isPathActive(tab.path);

              return (
                <Link
                  key={tab.path}
                  to={tab.path}
                  style={{
                    ...styles.sectionTab,
                    ...(selected ? styles.sectionTabActive : {}),
                  }}
                >
                  <span>{tab.label}</span>
                  <span
                    style={{
                      ...styles.sectionTabUnderline,
                      ...(selected ? {} : styles.sectionTabUnderlineInactive),
                    }}
                  />
                </Link>
              );
            })}
          </div>
        )}
        {children}
      </main>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    position: 'relative' as const,
  },
  stagingBanner: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: '#ff9800',
    color: '#000',
    padding: '0.5rem',
    textAlign: 'center' as const,
    fontWeight: 'bold' as const,
    fontSize: '0.9rem',
    zIndex: 9999,
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  },
  sidebar: {
    width: '250px',
    backgroundColor: colors.primary,
    color: colors.white,
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column' as const,
    position: 'fixed' as const,
    height: '100vh',
    left: 0,
    top: 0,
    paddingTop: '3rem',
    zIndex: 1000,
  },
  logo: {
    marginBottom: '2rem',
    paddingBottom: '1rem',
    borderBottom: `1px solid ${colors.white}33`,
  },
  logoText: {
    margin: 0,
    fontSize: '1.5rem',
  },
  nav: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    flex: 1,
  },
  navLink: {
    display: 'block',
    padding: '0.75rem 1rem',
    color: colors.white,
    textDecoration: 'none',
    borderRadius: '4px',
    marginBottom: '0.5rem',
    transition: 'background-color 0.2s',
  },
  activeLink: {
    backgroundColor: colors.secondary,
  },
  sectionTabs: {
    display: 'flex',
    gap: '0.75rem',
    marginBottom: '1.5rem',
  },
  sectionTab: {
    display: 'inline-flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '0.65rem',
    color: colors.textLight,
    padding: '0.25rem 1.2rem 0',
    fontWeight: 700,
    fontSize: '1.08rem',
    lineHeight: 1.2,
    textDecoration: 'none',
  },
  sectionTabActive: {
    color: colors.primary,
  },
  sectionTabUnderline: {
    width: '100%',
    height: '3px',
    backgroundColor: colors.primary,
    display: 'block',
  },
  sectionTabUnderlineInactive: {
    backgroundColor: 'transparent',
  },
  userInfo: {
    marginTop: 'auto',
    paddingTop: '1rem',
    borderTop: `1px solid ${colors.white}33`,
  },
  userName: {
    margin: '0 0 0.5rem 0',
    fontSize: '0.9rem',
    fontWeight: 'bold',
  },
  userPosition: {
    margin: '0 0 0.5rem 0',
    fontSize: '0.8rem',
    opacity: 0.8,
  },
  superuserBadge: {
    display: 'inline-block',
    padding: '0.2rem 0.5rem',
    backgroundColor: colors.secondary,
    fontSize: '0.7rem',
    borderRadius: '3px',
    marginBottom: '1rem',
  },
  logoutBtn: {
    width: '100%',
    padding: '0.5rem',
    backgroundColor: 'transparent',
    color: colors.white,
    border: `1px solid ${colors.white}`,
    borderRadius: '4px',
    cursor: 'pointer',
  },
  main: {
    flex: 1,
    padding: '1rem',
    backgroundColor: colors.background,
    marginLeft: '250px',
    marginTop: '2.5rem',
  },
};

export default Layout;
